import React from "react";
import { db } from "../firebase_setup/firebase";

import {
  collection,
  orderBy,
  limit,
  getDocs,
  query,
  startAfter,
  startAt,
  where,
  getCountFromServer
} from "firebase/firestore";

export const useFirestorePagination = ({
  searchTerm,
  paginationStates,
  direction,
  collectionDB,
  orderByElem,
  limitRange = 20
}) => {

  const [cursors, setCursors] = React.useState([]);

  const baseRef = React.useMemo(() => collection(db, collectionDB), [collectionDB]);

  const formattedSearch = searchTerm?.trim().toUpperCase() || "";
  const words = formattedSearch.split(" ");
  const mainWord = words[0] || "";

  const isLinea =
    mainWord === "CABALLERO" ||
    mainWord === "DAMA" ||
    mainWord === "UNISEX" ||
    mainWord === "INFANTIL";

  const buildSearchRange = (field) => [
    orderBy(field),
    where(field, ">=", mainWord),
    where(field, "<=", mainWord + "\uf8ff"),
    orderBy("existencias", "desc"),
  ];

  const applyPagination = (baseQuery) => {

    if (direction === "next" && cursors[paginationStates.current_page - 1]) {
      return query(
        baseQuery,
        startAfter(cursors[paginationStates.current_page - 1]),
        limit(limitRange)
      );
    }

    if (direction === "prev" && paginationStates.current_page > 0) {
      return query(
        baseQuery,
        startAt(cursors[paginationStates.current_page - 1]),
        limit(limitRange)
      );
    }

    return query(baseQuery, limit(limitRange));
  };

  const getQueryDoc = async () => {
    if (formattedSearch !== "" && formattedSearch.length < 3) {
      return {
        empty: true,
        docs: [],
        forEach: () => {}
      };
    }

    let baseSearchQuery;
    let querySelector;
    let skuSnap;
    let refSnap;

    if (formattedSearch && formattedSearch.length >= 3) {
      // 🔹 búsqueda directa SKU
      skuSnap = await getDocs(query(
        baseRef,
        where("sku", "==", formattedSearch),
        limit(1)
      ));

      if (skuSnap.empty && !isNaN(Number(formattedSearch))) {
        skuSnap = await getDocs(query(
          baseRef,
          where("sku", "==", Number(formattedSearch)),
          limit(1)
        ));
      }

      if (!skuSnap.empty) return skuSnap;

      // 🔹 búsqueda directa referencia

      refSnap = await getDocs(query(
        baseRef,
        where("referencia", "==", formattedSearch),
        limit(1)
      ));

      if (refSnap.empty && !isNaN(Number(formattedSearch))) {
        refSnap = await getDocs(query(
          baseRef,
          where("referencia", "==", Number(formattedSearch)),
          limit(1)
        ));
      }

      if (!refSnap.empty) return refSnap;

      if (isLinea) {
        baseSearchQuery = query(
          baseRef,
          ...buildSearchRange("linea")
        );
      } else {

        // 🔹 detectar si es marca
        const brandCheckSnap = await getDocs(query(
          baseRef,
          orderBy("marca"),
          where("marca", ">=", mainWord),
          where("marca", "<=", mainWord + "\uf8ff"),
          limit(1)
        ));

        const isBrand = !brandCheckSnap.empty;

        baseSearchQuery = query(
          baseRef,
          ...buildSearchRange(isBrand ? "marca" : "descripcion_corta")
        );
      }

      querySelector = applyPagination(baseSearchQuery);

    } else {
      // 🔹 navegación general
      baseSearchQuery = query(
        baseRef,
        orderBy("existencias", "desc"),
        orderBy(orderByElem)
      );

      querySelector = applyPagination(baseSearchQuery);
    }

    const resp = await getDocs(querySelector);

    if (resp.docs.length === 0) return resp;

    const lastDoc = resp.docs[resp.docs.length - 1];

    setCursors(prev => {
      const newCursors = [...prev];
      newCursors[paginationStates.current_page] = lastDoc;
      return newCursors;
    });

    return resp;
  };

  const getPaginationCount = async () => {
    try {
      if (formattedSearch && formattedSearch.length >= 3) {
        let countQuery;
        let skuSnap;
        let refSnap;

        // 🔹 SKU
        skuSnap = await getDocs(query(
          baseRef,
          where("sku", "==", formattedSearch),
          limit(1)
        ));

        if (skuSnap.empty && !isNaN(Number(formattedSearch))) {
          skuSnap = await getDocs(query(
            baseRef,
            where("sku", "==", Number(formattedSearch)),
            limit(1)
          ));
        }

        if (!skuSnap.empty) {
          return { total_reg: 1, total_pages: 1 };
        }

        // 🔹 referencia
        refSnap = await getDocs(query(
          baseRef,
          where("referencia", "==", formattedSearch),
          limit(1)
        ));

        if (refSnap.empty && !isNaN(Number(formattedSearch))) {
          refSnap = await getDocs(query(
            baseRef,
            where("referencia", "==", Number(formattedSearch)),
            limit(1)
          ));
        }

        if (!refSnap.empty) {
          return { total_reg: 1, total_pages: 1 };
        }

        if (isLinea) {
          countQuery = query(
            baseRef,
            orderBy("linea"),
            where("linea", ">=", mainWord),
            where("linea", "<=", mainWord + "\uf8ff")
          );
        } else {
          const brandCheckSnap = await getDocs(query(
            baseRef,
            where("marca", ">=", formattedSearch),
            where("marca", "<=", formattedSearch + "\uf8ff"),
            limit(1)
          ));

          const isBrand = !brandCheckSnap.empty;

          countQuery = query(
            baseRef,
            orderBy(isBrand ? "marca": "descripcion_corta"),
            where(isBrand ? "marca": "descripcion_corta", ">=", mainWord),
            where(isBrand ? "marca": "descripcion_corta", "<=", mainWord + "\uf8ff")
          );
        }
        const totalSnap = await getCountFromServer(countQuery);
        const total = totalSnap.data().count;

        return {
          total_reg: total,
          total_pages: Math.ceil(total / limitRange)
        };
      }
      // 🔹 navegación general
      const totalSnap = await getCountFromServer(baseRef);
      const total = totalSnap.data().count;

      return {
        total_reg: total,
        total_pages: Math.ceil(total / limitRange)
      };

    } catch{
      return {
        total_reg: 0,
        total_pages: 0
      };
    }
  };

  React.useEffect(() => {
    setCursors([]);
  }, [formattedSearch]);

  return {
    getQueryDoc,
    getPaginationCount
  };
};